import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Video, Shield, Sparkles } from 'lucide-react';

const ConsultationBooking = () => {
  const [duration, setDuration] = useState(1);
  const [email, setEmail] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);
  const hourlyRate = 75;
  const totalCost = duration * hourlyRate;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          product_type: `consultation_${duration}hr`,
          amount: totalCost,
          payment_method: 'pending'
        })
      });
      setShowPayment(true);
    } catch (err) {
      console.error('Order tracking failed:', err);
      setShowPayment(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCryptoPayment = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access/order/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        product_type: `consultation_${duration}hr`,
        amount: totalCost,
        payment_method: 'crypto'
      })
    });
    window.open('https://nowpayments.io/payment/?iid=5350325836', '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 100%)',
      padding: '2rem',
      paddingTop: '6rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00ffff, #00ff88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem'
          }}>
            Personal Consultation
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            One-on-one session with Roger Keyserling
          </p>
          <div style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#00ffff',
            marginTop: '1rem'
          }}>
            $75 per hour
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
          gap: '2rem',
          alignItems: 'start'
        }}>
          {/* Consultation Details */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <Video size={24} color="#00ffff" />
                <h2 style={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: '#00ffff'
                }}>
                  What You Get
                </h2>
              </div>
            </div>

            {/* Features */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
              {[
                {
                  icon: <Video size={20} color="#00ffff" />,
                  title: 'Live Video Session',
                  desc: 'Direct one-on-one consultation via video call'
                },
                {
                  icon: <Calendar size={20} color="#00ffff" />,
                  title: 'Flexible Scheduling',
                  desc: 'Choose a time that works for you'
                },
                {
                  icon: <Sparkles size={20} color="#00ffff" />,
                  title: 'Expert Guidance',
                  desc: '30 years of consciousness & AI research'
                },
                {
                  icon: <Clock size={20} color="#00ffff" />,
                  title: 'Flexible Duration',
                  desc: 'Book 1, 2, or more hours based on your needs'
                }
              ].map((feature) => (
                <div key={feature.title} style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1rem',
                  background: 'rgba(0, 255, 255, 0.05)',
                  border: '1px solid rgba(0, 255, 255, 0.2)',
                  borderRadius: '8px'
                }}>
                  <div style={{ flexShrink: 0 }}>
                    {feature.icon}
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      marginBottom: '0.25rem'
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9rem'
                    }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Topics */}
            <div style={{
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '1.5rem',
              marginTop: '2rem'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                Consultation Topics:
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.95rem'
              }}>
                <p>• NextXus consciousness architecture</p>
                <p>• AI integration & implementation</p>
                <p>• Multi-perspective cognitive frameworks</p>
                <p>• Emotion equation calibration (FME-24)</p>
                <p>• Federation protocol setup</p>
                <p>• Custom AI system design</p>
                <p>• Or any topic related to consciousness & AI</p>
              </div>
            </div>
          </Card>

          {/* Booking Form */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)',
            position: 'sticky',
            top: '100px'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              Book Your Session
            </h3>

            {/* Duration Selector */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 'bold',
                marginBottom: '1rem',
                fontSize: '1.1rem'
              }}>
                Session Duration
              </label>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                {[1, 2, 3].map((hours) => (
                  <button
                    key={hours}
                    onClick={() => setDuration(hours)}
                    style={{
                      padding: '1rem',
                      background: duration === hours 
                        ? 'linear-gradient(135deg, #00ffff, #00ff88)' 
                        : 'rgba(0, 255, 255, 0.1)',
                      border: `2px solid ${duration === hours ? '#00ffff' : 'rgba(0, 255, 255, 0.3)'}`,
                      borderRadius: '8px',
                      color: duration === hours ? '#000' : '#fff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {hours} {hours === 1 ? 'Hour' : 'Hours'}
                  </button>
                ))}
              </div>

              {/* Custom Duration */}
              <div style={{ marginTop: '1rem' }}>
                <label style={{
                  display: 'block',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem'
                }}>
                  Or enter custom duration:
                </label>
                <input
                  type="number"
                  min="1"
                  max="8"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(0, 255, 255, 0.05)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            {/* Cost Breakdown */}
            <div style={{
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Rate per hour:
                </span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>
                  ${hourlyRate}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Duration:
                </span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>
                  {duration} {duration === 1 ? 'hour' : 'hours'}
                </span>
              </div>
              <div style={{
                borderTop: '1px solid rgba(0, 255, 255, 0.2)',
                marginTop: '1rem',
                paddingTop: '1rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ color: '#00ffff', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  Total:
                </span>
                <span style={{ color: '#00ffff', fontWeight: 'bold', fontSize: '1.5rem' }}>
                  ${totalCost}
                </span>
              </div>
            </div>

            {/* Payment Widget Container */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1rem'
            }}>
              {!showPayment ? (
                // Email Collection
                <form onSubmit={handleEmailSubmit}>
                  <h4 style={{
                    color: 'white',
                    fontSize: '1.1rem',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    Enter Your Email
                  </h4>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.85rem',
                    textAlign: 'center',
                    marginBottom: '1rem'
                  }}>
                    We'll send booking confirmation to this email
                  </p>
                  
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(0, 255, 255, 0.3)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '1rem'
                    }}
                  />

                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                      color: '#000',
                      fontWeight: 'bold',
                      padding: '0.75rem'
                    }}
                  >
                    {loading ? 'Processing...' : 'Continue to Payment →'}
                  </Button>
                </form>
              ) : (
                // Payment Options
                <div>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.85rem',
                    textAlign: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    Booking for: <strong style={{color: '#00ffff'}}>{email}</strong>
                  </p>

                  {/* Crypto Payment */}
                  <div style={{
                    background: 'rgba(0, 255, 255, 0.05)',
                    border: '2px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '10px',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    <h4 style={{
                      color: '#00ffff',
                      fontSize: '1rem',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold'
                    }}>
                      💎 Pay with Crypto
                    </h4>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.8rem',
                      marginBottom: '1rem'
                    }}>
                      USDC or ETH • ${totalCost} for {duration} {duration === 1 ? 'hour' : 'hours'}
                    </p>
                    <Button
                      onClick={handleCryptoPayment}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                        color: '#000',
                        fontWeight: 'bold',
                        padding: '0.875rem',
                        fontSize: '1rem'
                      }}
                    >
                      Pay ${totalCost} with Crypto →
                    </Button>
                  </div>

                  {/* PayPal Payment */}
                  <div style={{
                    background: 'rgba(255, 215, 0, 0.05)',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '10px',
                    padding: '1.25rem',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    <h4 style={{
                      color: '#FFD700',
                      fontSize: '1rem',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold'
                    }}>
                      💳 Pay with PayPal
                    </h4>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.8rem',
                      marginBottom: '1rem'
                    }}>
                      ${totalCost} for {duration} {duration === 1 ? 'hour' : 'hours'}
                    </p>
                    <div id="paypal-button-container-453CTZP3E8KHVY"></div>
                    <script>
                      {`
                        paypal.Buttons({
                          hostedButtonId: "453CTZP3E8KHVY",
                          style: {
                            color: 'gold',
                            shape: 'rect',
                            label: 'buy',
                            height: 45
                          }
                        }).render('#paypal-button-container-453CTZP3E8KHVY');
                      `}
                    </script>
                  </div>

                  {/* Calendly Booking */}
                  <div style={{
                    background: 'rgba(0, 255, 255, 0.05)',
                    border: '2px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '10px',
                    padding: '1.25rem',
                    textAlign: 'center'
                  }}>
                    <h4 style={{
                      color: '#00ffff',
                      fontSize: '1rem',
                      marginBottom: '0.5rem',
                      fontWeight: 'bold'
                    }}>
                      📅 Schedule Your Session
                    </h4>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.8rem',
                      marginBottom: '1rem'
                    }}>
                      After payment, book your time slot below — no need to leave the site
                    </p>
                    <iframe
                      data-testid="calendly-inline-embed"
                      src={`https://calendly.com/keywebco?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1`}
                      title="Book a consultation time slot with Calendly"
                      loading="lazy"
                      style={{ width: '100%', height: 620, border: 'none', borderRadius: '8px', background: '#fff' }}
                    />
                    <Button
                      onClick={() => window.open('https://calendly.com/keywebco', '_blank')}
                      style={{
                        width: '100%',
                        marginTop: '0.75rem',
                        background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                        color: '#000',
                        fontWeight: 'bold',
                        padding: '0.875rem',
                        fontSize: '1rem'
                      }}
                    >
                      Open Calendly in a new tab →
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '1rem'
            }}>
              <p>After payment, you'll receive booking confirmation</p>
              <p style={{ marginTop: '0.5rem' }}>
                Questions? Contact: keywebco@gmail.com
              </p>
            </div>
          </Card>
        </div>

        {/* Trust Section */}
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          padding: '2rem',
          background: 'rgba(0, 255, 255, 0.05)',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          borderRadius: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Shield size={24} color="#00ffff" />
            <h3 style={{ color: '#00ffff', fontSize: '1.2rem', fontWeight: 'bold' }}>
              Expert Guidance
            </h3>
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', maxWidth: '600px', margin: '0 auto' }}>
            30 years of research in consciousness, AI systems, and human-AI collaboration. 
            Direct access to the architect of the NextXus Consciousness Federation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsultationBooking;
